import React, { forwardRef } from "react";
import { Button } from "@mui/material";
import {
  fontSans,
  inkTertiary,
  marqueeGold,
  stageLight,
  studioBlack,
  surfaceControl,
  surfaceControlHover,
} from "../../theme/tokens";

/**
 * SecondaryCTA — the canonical tonal pill.
 *
 * Equal-weight alternative to PrimaryCTA: solid control-tier fill, no
 * border, 980px pill, 200ms ease-out-quart state changes. Used for the
 * macOS / Windows download buttons and any secondary choice that should
 * sit alongside a primary CTA without competing.
 */

const EASE = "cubic-bezier(0.25, 1, 0.5, 1)";

// Same box as PrimaryCTA at each size, so the two line up side by side.
const SIZE_STYLES = {
  md: {
    padding: { xs: "11px 24px", md: "13px 28px" },
    fontSize: { xs: "0.9375rem", md: "1rem" },
    minWidth: { xs: 200, md: 240 },
  },
  sm: {
    padding: { xs: "7px 18px", md: "8px 20px" },
    fontSize: "0.875rem",
    minWidth: undefined,
  },
};

const mergeSx = (base, extra) => {
  if (!extra) return base;
  if (Array.isArray(extra)) return [base, ...extra];
  return [base, extra];
};

const SecondaryCTA = forwardRef(function SecondaryCTA(
  {
    children,
    onClick,
    href,
    target,
    rel,
    startIcon,
    endIcon,
    disabled = false,
    fullWidth = false,
    type = "button",
    size = "md",
    sx,
    ...rest
  },
  ref,
) {
  const sizing = SIZE_STYLES[size] || SIZE_STYLES.md;

  const baseSx = {
    background: surfaceControl,
    color: stageLight,
    borderRadius: "980px",
    padding: sizing.padding,
    minWidth: sizing.minWidth,
    whiteSpace: "nowrap",
    fontFamily: fontSans,
    fontSize: sizing.fontSize,
    fontWeight: 500,
    lineHeight: 1.4,
    letterSpacing: "-0.005em",
    textTransform: "none",
    boxShadow: "none",
    transition: `background 200ms ${EASE}, box-shadow 200ms ${EASE}`,
    "&:hover": {
      background: surfaceControlHover,
      boxShadow: "none",
    },
    "&:focus-visible": {
      outline: "none",
      boxShadow: `0 0 0 2px ${studioBlack}, 0 0 0 4px ${marqueeGold}`,
    },
    "&.Mui-disabled": {
      background: surfaceControl,
      color: inkTertiary,
    },
  };

  return (
    <Button
      ref={ref}
      variant="contained"
      disableElevation
      disableRipple={false}
      onClick={onClick}
      href={href}
      target={target}
      rel={rel}
      startIcon={startIcon}
      endIcon={endIcon}
      disabled={disabled}
      fullWidth={fullWidth}
      type={href ? undefined : type}
      sx={mergeSx(baseSx, sx)}
      {...rest}
    >
      {children}
    </Button>
  );
});

export default SecondaryCTA;
export { SecondaryCTA };
